import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Zap, Database, TrendingUp, Filter } from "lucide-react";

const AEPowerData = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      category: "Source Data Fields",
      items: [
        {
          name: "powerOutput",
          source: "VesselAuxEngineData",
          description: "Actual power output measurement",
          calculation: "Direct value from database field"
        },
        {
          name: "averageReportedLoad",
          source: "VesselAuxEngineData",
          description: "Average load over measurement period",
          calculation: "Direct value from database field"
        },
        {
          name: "maxCapacityLoad",
          source: "VesselAuxEngineData",
          description: "Maximum design capacity",
          calculation: "Direct value or default of 550 kW"
        },
        {
          name: "engineId",
          source: "VesselAuxEngineData",
          description: "Auxiliary engine identifier",
          calculation: "Regex pattern matching for pipe-separated values"
        }
      ]
    },
    {
      category: "Calculated Fields",
      items: [
        {
          name: "powerOutput",
          source: "Daily average",
          description: "Average power output per day",
          calculation: "Sum of powerOutput ÷ record count"
        },
        {
          name: "averageReportedLoad",
          source: "Daily average",
          description: "Average reported load per day",
          calculation: "Sum of averageReportedLoad ÷ record count"
        },
        {
          name: "powerRatio",
          source: "Calculated ratio",
          description: "Load to capacity ratio",
          calculation: "averageReportedLoad ÷ maxCapacityLoad"
        }
      ]
    }
  ];

  const calculations = [
    {
      title: "Daily Average Power",
      formula: "∑ powerOutput ÷ count(records)",
      description: "Average power output for each day",
      parameters: ["Daily grouping", "Zero for days without data", "Rounded to 2 decimals"]
    },
    {
      title: "Power Ratio",
      formula: "averageReportedLoad ÷ maxCapacityLoad",
      description: "Ratio of actual load to maximum capacity",
      parameters: ["maxCapacityLoad > 0 required", "Zero if either value is zero", "Unitless ratio"]
    }
  ];

  const statisticsCalculations = [
    {
      metric: "Average Power",
      formula: "Total power output ÷ Number of days",
      purpose: "Mean power output across entire period"
    },
    {
      metric: "Maximum Power",
      formula: "Max(powerOutput across all days)",
      purpose: "Highest daily power output"
    },
    {
      metric: "Total Power Output",
      formula: "∑ powerOutput for all days",
      purpose: "Cumulative power generation"
    },
    {
      metric: "Average Load Ratio",
      formula: "∑ powerRatio ÷ Days with load data",
      purpose: "Average load to capacity ratio"
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#e9cbf5] rounded-lg">
              <Zap className="w-6 h-6 text-[#7d3c98]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Auxiliary Engine Power Data Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                Daily power output analysis with complete date sequence
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            
            {/* Data Sources */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data Sources & Fields
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dataSources.map((source, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-3">{source.category}</h4>
                    <div className="space-y-3">
                      {source.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-gray-800">{item.name}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {item.source}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{item.description}</p>
                          <p className="text-xs text-gray-500">Calculation: {item.calculation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculations */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Core Calculations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {calculations.map((calc, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{calc.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{calc.description}</p>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                      <p className="text-sm font-mono bg-gray-50 p-2 rounded border">
                        {calc.formula}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Parameters:</p>
                      <div className="flex flex-wrap gap-1">
                        {calc.parameters.map((param, paramIdx) => (
                          <span key={paramIdx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {param}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Statistics Calculations */}
            <div className="mb-8 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Summary Statistics Calculations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {statisticsCalculations.map((stat, idx) => (
                  <div key={idx} className="p-3 bg-white rounded border border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-blue-700">{idx + 1}</span>
                      </div>
                      <h4 className="font-medium text-blue-700">{stat.metric}</h4>
                    </div>
                    <div className="ml-9">
                      <p className="text-sm text-gray-700 mb-1">Formula:</p>
                      <code className="text-sm bg-gray-50 p-1 rounded border block">{stat.formula}</code>
                      <p className="text-sm text-gray-600 mt-2">{stat.purpose}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default AEPowerData;